/**
 * The languages the UI is translated into -- every language the app knows by
 * name (`LANGUAGES` in `@shared/lib/languages` names each of these in each of
 * these). Every catalog (`**\/i18n/<lang>.ts`) must have a file for each of
 * these, and every key in it -- `tsc` checks the keys, the build checks the
 * files. A tenant's `pwa.json` `languages` may only name languages from this
 * list: the build refuses anything else.
 *
 * Adding one: docs/wiki/I18n.md#how-to-add-a-new-language.
 *
 * Plain data with no imports on purpose: the build (scripts/lib/i18n) reads
 * this file directly with Node.
 */
export const SUPPORTED_UI_LANGUAGES = [
	'ar',
	'bg',
	'cs',
	'da',
	'de',
	'el',
	'en',
	'es',
	'et',
	'fi',
	'fr',
	'he',
	'hu',
	'id',
	'it',
	'ja',
	'ko',
	'lt',
	'lv',
	'nb',
	'nl',
	'pl',
	'pt',
	'ro',
	'ru',
	'sk',
	'sl',
	'sv',
	'th',
	'tr',
	'uk',
	'vi',
	'zh',
] as const;

export type UiLanguage = (typeof SUPPORTED_UI_LANGUAGES)[number];

export const SOURCE_LANGUAGE = 'en' satisfies UiLanguage;

/** Writing direction of each supported language. */
export const UI_LANGUAGE_DIRECTION: Record<UiLanguage, 'ltr' | 'rtl'> = {
	ar: 'rtl',
	bg: 'ltr',
	cs: 'ltr',
	da: 'ltr',
	de: 'ltr',
	el: 'ltr',
	en: 'ltr',
	es: 'ltr',
	et: 'ltr',
	fi: 'ltr',
	fr: 'ltr',
	he: 'rtl',
	hu: 'ltr',
	id: 'ltr',
	it: 'ltr',
	ja: 'ltr',
	ko: 'ltr',
	lt: 'ltr',
	lv: 'ltr',
	nb: 'ltr',
	nl: 'ltr',
	pl: 'ltr',
	pt: 'ltr',
	ro: 'ltr',
	ru: 'ltr',
	sk: 'ltr',
	sl: 'ltr',
	sv: 'ltr',
	th: 'ltr',
	tr: 'ltr',
	uk: 'ltr',
	vi: 'ltr',
	zh: 'ltr',
};

/**
 * Dev-only pseudo-locale: every English label accented and ~30% longer, so a
 * label that stays plain English on screen is still hardcoded, and a layout
 * that clips needs room for German or French. Never shipped.
 */
export const PSEUDO_LANGUAGE = 'en-XA';

export function isSupportedUiLanguage(language: string): language is UiLanguage {
	return (SUPPORTED_UI_LANGUAGES as ReadonlyArray<string>).includes(language);
}

export function uiLanguageDirection(language: string): 'ltr' | 'rtl' {
	return isSupportedUiLanguage(language) ? UI_LANGUAGE_DIRECTION[language] : 'ltr';
}
